import ky from 'ky';
import { detectAuthRuntime } from '@/features/auth/runtime';
import { useAuthStore } from '@/features/auth/store';

const BASE_URL = import.meta.env['VITE_API_URL'] ?? '/api';

function applyCommonHeaders(request: Request) {
  const runtime = useAuthStore.getState().runtime ?? detectAuthRuntime();
  request.headers.set('x-inhound-runtime', runtime);
}

/**
 * Base ky instance without auth.
 * Use for public endpoints like /auth/telegram-webapp.
 */
export const publicApiClient = ky.create({
  prefixUrl: BASE_URL,
  timeout: 15_000,
  retry: { limit: 1, statusCodes: [429, 503] },
  hooks: {
    afterResponse: [
      async (_input, _options, response) => {
        if (!response.ok) {
          // Let caller handle errors; just pass through
          return response;
        }
        return response;
      },
    ],
  },
});

/**
 * Authenticated ky instance.
 * Reads token from zustand store and injects Authorization header.
 * On 401 — clears auth state (re-login will happen on next app start).
 */
export const apiClient = publicApiClient.extend({
  hooks: {
    beforeRequest: [
      (request) => {
        applyCommonHeaders(request);
        const token = useAuthStore.getState().accessToken;
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      },
    ],
    afterResponse: [
      async (_input, _options, response) => {
        if (response.status === 401) {
          useAuthStore.getState().clearAuth();
        }
        return response;
      },
    ],
  },
});

/**
 * Optional-auth ky instance.
 * Adds Authorization when available, but is safe for public/guest routes.
 */
export const optionalAuthApiClient = publicApiClient.extend({
  hooks: {
    beforeRequest: [
      (request) => {
        applyCommonHeaders(request);
        const token = useAuthStore.getState().accessToken;
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      },
    ],
    afterResponse: [
      async (_input, _options, response) => {
        if (response.status === 401 && useAuthStore.getState().isAuthenticated) {
          useAuthStore.getState().clearAuth();
        }
        return response;
      },
    ],
  },
});
